/**
 * Movement Effect Renderer
 *
 * Renders visual effects for movement abilities (teleport, dash, charge, leap).
 * Handles sprite manipulation, tweening, and screen effects for dynamic movement.
 */

import * as PIXI from 'pixi.js';
import type { UnitMovedEventData } from '@drawn-of-war/shared';
import type { CombatGridRenderer } from '../../components/CombatGrid/CombatGridRenderer';
import type { AxialCoordinate } from '@drawn-of-war/shared';

/**
 * Movement Effect Renderer
 *
 * Handles visual effects for movement abilities based on movement type.
 * Creates smooth transitions, afterimages, screen shake, and impact effects.
 */
export class MovementEffectRenderer {
  private gridRenderer: CombatGridRenderer;

  constructor(gridRenderer: CombatGridRenderer) {
    this.gridRenderer = gridRenderer;
  }

  /**
   * Play movement effect based on movement type
   * Main entry point for rendering movement ability visuals
   */
  public playMovementEffect(data: UnitMovedEventData): void {
    console.log('[MovementEffectRenderer] Playing movement effect:', {
      unitId: data.unitId,
      movementType: data.movementType,
      from: data.fromPosition,
      to: data.toPosition
    });

    switch (data.movementType) {
      case 'teleport':
        this.playTeleportEffect(data);
        break;
      case 'dash':
        this.playDashEffect(data);
        break;
      case 'charge':
        this.playChargeEffect(data);
        break;
      case 'leap':
        this.playLeapEffect(data);
        break;
      default:
        console.warn('[MovementEffectRenderer] Unknown movement type:', data.movementType);
    }
  }

  /**
   * Teleport effect: fade out at origin, snap position, fade in at destination
   */
  private playTeleportEffect(data: UnitMovedEventData): void {
    const sprite = this.getUnitSprite(data.unitId);
    if (!sprite) {
      console.warn('[MovementEffectRenderer] Cannot play teleport effect - sprite not found:', data.unitId);
      return;
    }

    // Get destination pixel position
    const toPixel = this.hexToPixel(data.toPosition);

    // Phase 1: Fade out at origin (100ms)
    this.tweenAlpha(sprite.container, 1.0, 0.0, 100, () => {
      // Phase 2: Snap position
      sprite.container.x = toPixel.x;
      sprite.container.y = toPixel.y;

      // Phase 3: Fade in at destination (100ms)
      this.tweenAlpha(sprite.container, 0.0, 1.0, 100);
    });
  }

  /**
   * Dash effect: create afterimages along the path that fade out
   */
  private playDashEffect(data: UnitMovedEventData): void {
    const sprite = this.getUnitSprite(data.unitId);
    if (!sprite) {
      console.warn('[MovementEffectRenderer] Cannot play dash effect - sprite not found:', data.unitId);
      return;
    }

    const fromPixel = this.hexToPixel(data.fromPosition);
    const toPixel = this.hexToPixel(data.toPosition);

    // Create 4 afterimages evenly spaced along the path
    const afterimageCount = 4;
    const afterimages: PIXI.Container[] = [];

    for (let i = 0; i < afterimageCount; i++) {
      const progress = (i + 1) / (afterimageCount + 1);
      const x = fromPixel.x + (toPixel.x - fromPixel.x) * progress;
      const y = fromPixel.y + (toPixel.y - fromPixel.y) * progress;

      // Clone the sprite for afterimage
      const afterimage = this.createAfterimage(sprite.sprite, x, y);
      if (afterimage) {
        afterimages.push(afterimage);

        // Add to creature layer
        const creatureLayer = (this.gridRenderer as any).creatureLayer;
        if (creatureLayer) {
          creatureLayer.addChild(afterimage);
        }

        // Fade out afterimage over 150ms with staggered start
        const delay = i * 20; // Stagger by 20ms each
        setTimeout(() => {
          this.tweenAlpha(afterimage, 0.5, 0.0, 150, () => {
            // Cleanup: remove afterimage from stage
            if (afterimage.parent) {
              afterimage.parent.removeChild(afterimage);
            }
            afterimage.destroy({ children: true });
          });
        }, delay);
      }
    }

    // Move the main sprite to destination
    sprite.container.x = toPixel.x;
    sprite.container.y = toPixel.y;
  }

  /**
   * Charge effect: similar to dash but with screen shake at the end
   */
  private playChargeEffect(data: UnitMovedEventData): void {
    // First, play the dash effect (afterimages)
    this.playDashEffect(data);

    // Add screen shake at destination after a delay
    setTimeout(() => {
      this.playScreenShake(5, 100); // 5px intensity, 100ms duration
      console.log('[MovementEffectRenderer] Charge impact - screen shake triggered');
    }, 100);
  }

  /**
   * Leap effect: arc animation (y-offset) with landing impact
   */
  private playLeapEffect(data: UnitMovedEventData): void {
    const sprite = this.getUnitSprite(data.unitId);
    if (!sprite) {
      console.warn('[MovementEffectRenderer] Cannot play leap effect - sprite not found:', data.unitId);
      return;
    }

    const fromPixel = this.hexToPixel(data.fromPosition);
    const toPixel = this.hexToPixel(data.toPosition);

    // Calculate total duration (default 300ms for leap)
    const duration = 300;
    const arcHeight = -80; // Jump up 80 pixels at peak
    const startTime = performance.now();

    let animationFrame: number;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1.0);

      // Parabolic arc: y = -4h * t * (t - 1)
      // This gives a smooth arc from 0 to arcHeight and back to 0
      const yOffset = arcHeight * -4 * progress * (progress - 1);

      // Linear interpolation for x/y position
      const baseX = fromPixel.x + (toPixel.x - fromPixel.x) * progress;
      const baseY = fromPixel.y + (toPixel.y - fromPixel.y) * progress;

      // Apply arc offset
      sprite.container.x = baseX;
      sprite.container.y = baseY + yOffset;

      if (progress < 1.0) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        // Ensure final position is exact
        sprite.container.x = toPixel.x;
        sprite.container.y = toPixel.y;

        // Landing impact effect
        this.playLandingImpact(toPixel);
      }
    };

    animationFrame = requestAnimationFrame(animate);
  }

  /**
   * Play landing impact effect (dust particles expanding outward)
   */
  private playLandingImpact(position: { x: number; y: number }): void {
    const impactContainer = new PIXI.Container();
    impactContainer.x = position.x;
    impactContainer.y = position.y;

    const creatureLayer = (this.gridRenderer as any).creatureLayer;
    if (!creatureLayer) {
      console.warn('[MovementEffectRenderer] Cannot play landing impact - creature layer not found');
      return;
    }

    creatureLayer.addChild(impactContainer);

    // Create 8 dust particles radiating outward
    const particleCount = 8;
    const particles: { sprite: PIXI.Graphics; vx: number; vy: number }[] = [];

    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const particle = new PIXI.Graphics();

      particle.circle(0, 0, 4);
      particle.fill({ color: 0xCCCCCC, alpha: 0.6 });

      impactContainer.addChild(particle);

      // Velocity radiating outward
      const speed = 80; // pixels per second
      particles.push({
        sprite: particle,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed
      });
    }

    // Animate particles
    const startTime = performance.now();
    const duration = 300; // 300ms

    let animationFrame: number;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1.0);
      const deltaTime = 16.67 / 1000; // Assume 60fps for simplicity

      // Update particle positions
      for (const p of particles) {
        p.sprite.x += p.vx * deltaTime;
        p.sprite.y += p.vy * deltaTime;
        p.sprite.alpha = 0.6 * (1 - progress);
      }

      if (progress < 1.0) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        // Cleanup
        if (impactContainer.parent) {
          impactContainer.parent.removeChild(impactContainer);
        }
        impactContainer.destroy({ children: true });
      }
    };

    animationFrame = requestAnimationFrame(animate);
  }

  /**
   * Play screen shake effect
   */
  private playScreenShake(intensity: number, duration: number): void {
    // Check if gridRenderer has camera shake method (it may not be implemented yet)
    const app = this.gridRenderer.getApp();
    if (!app || !app.stage) {
      console.log('[MovementEffectRenderer] Screen shake requested but no app.stage available');
      return;
    }

    const stage = app.stage;
    const originalX = stage.x;
    const originalY = stage.y;
    const startTime = performance.now();

    let animationFrame: number;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1.0);

      if (progress < 1.0) {
        // Random offset decreasing over time
        const currentIntensity = intensity * (1 - progress);
        stage.x = originalX + (Math.random() - 0.5) * currentIntensity * 2;
        stage.y = originalY + (Math.random() - 0.5) * currentIntensity * 2;

        animationFrame = requestAnimationFrame(animate);
      } else {
        // Reset to original position
        stage.x = originalX;
        stage.y = originalY;
      }
    };

    animationFrame = requestAnimationFrame(animate);
  }

  /**
   * Create an afterimage sprite (semi-transparent clone)
   */
  private createAfterimage(
    sprite: PIXI.AnimatedSprite | PIXI.Sprite,
    x: number,
    y: number
  ): PIXI.Container | null {
    try {
      // Get current texture from sprite
      let texture: PIXI.Texture | undefined;
      if (sprite instanceof PIXI.AnimatedSprite) {
        // AnimatedSprite.textures can contain Texture or FrameObject
        const frameData = sprite.textures[sprite.currentFrame] || sprite.textures[0];
        // Check if it's a FrameObject (has texture property) or a Texture directly
        if (frameData && 'texture' in frameData) {
          texture = (frameData as { texture: PIXI.Texture }).texture;
        } else {
          texture = frameData as PIXI.Texture | undefined;
        }
      } else {
        texture = sprite.texture;
      }

      if (!texture) {
        console.warn('[MovementEffectRenderer] Cannot create afterimage - no texture available');
        return null;
      }

      // Create sprite clone
      const afterimageSprite = new PIXI.Sprite(texture);
      afterimageSprite.anchor.set(0.5);
      afterimageSprite.scale.copyFrom(sprite.scale);
      afterimageSprite.rotation = sprite.rotation;

      // Create container to match original structure
      const container = new PIXI.Container();
      container.addChild(afterimageSprite);
      container.x = x;
      container.y = y;
      container.alpha = 0.5; // Semi-transparent

      return container;
    } catch (error) {
      console.error('[MovementEffectRenderer] Error creating afterimage:', error);
      return null;
    }
  }

  /**
   * Simple tween for alpha (opacity) changes
   */
  private tweenAlpha(
    target: PIXI.Container,
    fromAlpha: number,
    toAlpha: number,
    duration: number,
    onComplete?: () => void
  ): void {
    const startTime = performance.now();
    let animationFrame: number;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1.0);

      // Linear interpolation
      target.alpha = fromAlpha + (toAlpha - fromAlpha) * progress;

      if (progress < 1.0) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        // Ensure final value is exact
        target.alpha = toAlpha;
        if (onComplete) {
          onComplete();
        }
      }
    };

    animationFrame = requestAnimationFrame(animate);
  }

  /**
   * Get unit sprite container by unitId
   */
  private getUnitSprite(unitId: string): any {
    return this.gridRenderer.getSpriteByUnitId(unitId);
  }

  /**
   * Convert hex coordinates to pixel coordinates
   */
  private hexToPixel(coord: AxialCoordinate): { x: number; y: number } {
    return this.gridRenderer.hexToPixel(coord);
  }
}
